import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Slider } from '../components/ui/slider';
import { 
  Zap, 
  ArrowLeft,
  LogOut,
  MapPin,
  Users,
  Euro
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CreateCommunity = () => {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    location: '',
    price_per_kwh: 0.15,
    max_members: 50,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      toast.error('Connectez-vous pour créer une communauté');
      navigate('/login');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${API_URL}/api/communities`, formData);
      toast.success('Communauté créée avec succès !');
      navigate(`/communities/${response.data.id}`);
    } catch (error) {
      console.error('Error creating community:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Déconnexion réussie');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <img src="/logo.png" alt="Green Teva" className="w-10 h-10 rounded-xl object-cover" />
              <span className="text-xl font-bold text-primary">Green Teva</span>
            </Link>
            
            <div className="hidden md:flex items-center gap-6">
              <Link to="/dashboard" className="nav-link">Dashboard</Link>
              <Link to="/communities" className="nav-link active">Communautés</Link>
              <Link to="/simulator" className="nav-link">Simulateur</Link>
            </div>
            
            <div className="flex items-center gap-4">
              {isAuthenticated && (
                <>
                  <div className="hidden sm:block text-right">
                    <p className="text-sm font-medium text-foreground">{user?.name}</p>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={handleLogout}
                    className="text-muted-foreground hover:text-foreground"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-5 h-5" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-6">
        <div className="max-w-2xl mx-auto">
          {/* Back Link */}
          <Link 
            to="/communities"
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
            data-testid="back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour aux communautés
          </Link>

          <Card className="form-card">
            <CardHeader>
              <CardTitle className="text-2xl">Créer une communauté</CardTitle>
              <CardDescription>
                Lancez votre coopérative énergétique en quelques minutes
              </CardDescription>
            </CardHeader>
            
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Nom de la communauté</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Ex: Énergie Solaire du Quartier"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="input-green"
                    required
                    data-testid="name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    placeholder="Décrivez votre communauté, ses objectifs..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="input-green min-h-[100px]"
                    required
                    data-testid="description-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="location" className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Localisation
                  </Label>
                  <Input
                    id="location"
                    type="text"
                    placeholder="Ex: Lyon 3ème, 69003"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="input-green"
                    required
                    data-testid="location-input"
                  />
                </div>
                
                <div className="space-y-4">
                  <Label className="flex items-center gap-2">
                    <Euro className="w-4 h-4" />
                    Prix par kWh: <span className="font-bold text-primary">{formData.price_per_kwh.toFixed(2)} €</span>
                  </Label>
                  <Slider
                    value={[formData.price_per_kwh]}
                    onValueChange={(value) => setFormData({ ...formData, price_per_kwh: value[0] })}
                    min={0.05}
                    max={0.30}
                    step={0.01}
                    className="py-4"
                    data-testid="price-slider"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>0.05 €</span>
                    <span>0.30 €</span>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <Label className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Nombre maximum de membres: <span className="font-bold text-primary">{formData.max_members}</span>
                  </Label>
                  <Slider
                    value={[formData.max_members]}
                    onValueChange={(value) => setFormData({ ...formData, max_members: value[0] })}
                    min={2}
                    max={100}
                    step={1}
                    className="py-4"
                    data-testid="members-slider"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>2 membres</span>
                    <span>100 membres</span>
                  </div>
                </div>
                
                <div className="p-4 bg-muted/50 rounded-xl">
                  <h4 className="font-medium text-foreground mb-2">Récapitulatif</h4>
                  <ul className="space-y-1 text-sm text-muted-foreground">
                    <li>• Vous serez automatiquement administrateur de la communauté</li>
                    <li>• Les membres pourront rejoindre jusqu'à {formData.max_members} personnes</li>
                    <li>• Prix fixé à {formData.price_per_kwh.toFixed(2)} €/kWh</li>
                    <li>• Création gratuite, sans engagement</li>
                  </ul>
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full btn-primary"
                  disabled={loading}
                  data-testid="create-submit-btn"
                >
                  {loading ? (
                    <div className="loader w-5 h-5 border-2 border-white/30 border-t-white" />
                  ) : (
                    'Créer ma communauté'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default CreateCommunity;
